import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { Alert, Flex, Modal, Switch, TimePicker, Typography } from 'antd'
import dayjs from 'dayjs'
import { Formik } from 'formik'
import CustomAntButton from 'general/components/Button/CustomAntButton'
import CustomDataTable from 'general/components/CustomDataTable'
import CustomAntInput from 'general/components/Forms/CustomAntInput/CustomAntInput'
import CustomAntSelect from 'general/components/CustomAntSelect'
import ModalConfirm from 'general/components/Modal/ModalConfirm'
import { SYSTEM_TYPE_INVOICE, TYPE_TIME } from 'general/constants/AppConstants'
import AppData from 'general/constants/AppData'
import ToastHelper from 'general/helpers/ToastHelper'
import useGetSerials from 'hooks/Queries/useGetSerials'
import { useAppDispatch } from 'hooks/useRedux'
import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import {
  getDraftBeforeConfigCount,
  getInvoiceAutoConfigHistory,
  getInvoiceAutoConfig,
  updateInvoiceAutoConfig,
} from '../../asyncThunks'
import { useSystemConfigSelector } from '../../SystemConfigurationSlice'
import FooterCard from '../FooterCard'

const ALL_SERIALS_VALUE = '__ALL__'
const HISTORY_FIELD_LABELS = {
  isAuto: 'Tự động phát hành',
  serials: 'Mẫu hóa đơn',
  timeLog: 'Thời gian chờ xuất',
  typeTime: 'Cơ chế phát hành',
  timeMoment: 'Khung giờ phát hành',
}
const HISTORY_IGNORED_FIELDS = new Set([
  'accountId',
  'classify',
  'companyId',
  'createdAt',
  'invoiceAutoConfigId',
  'sendTaxMTT',
  'typeInvoice',
  'customerName',
  'allSerials',
  'updatedAt',
])

function getHistoryFieldValue(config, field) {
  if (field === 'serials') {
    return config?.allSerials
      ? 'Tất cả'
      : normalizeHistoryValue(config?.serials)
  }

  if (field === 'timeLog' && config?.typeTime !== TYPE_TIME.LOG) {
    return undefined
  }

  if (field === 'timeMoment' && config?.typeTime !== TYPE_TIME.MOMENT) {
    return undefined
  }

  return normalizeHistoryValue(config?.[field])
}

function normalizeHistoryValue(value) {
  if (
    typeof value === 'string' &&
    (value.startsWith('[') || value.startsWith('{'))
  ) {
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  return value
}

function formatHistoryValue(field, value) {
  const normalizedValue = value

  if (
    normalizedValue === null ||
    normalizedValue === undefined ||
    normalizedValue === ''
  ) {
    return '-'
  }

  if (field === 'isAuto') {
    return normalizedValue ? 'Có' : 'Không'
  }

  if (Array.isArray(normalizedValue)) {
    return normalizedValue.join(', ')
  }

  if (field === 'typeTime') {
    return (
      {
        [TYPE_TIME.LOG]: 'Chờ xuất',
        [TYPE_TIME.MOMENT]: 'Theo khung giờ',
      }[normalizedValue] ?? String(normalizedValue)
    )
  }

  if (typeof normalizedValue === 'object') {
    return JSON.stringify(normalizedValue)
  }

  return String(normalizedValue)
}

function getHistoryChanges(record) {
  const beforeData = record?.beforeData ?? {}
  const afterData = record?.afterData ?? {}
  const fields = new Set([
    ...Object.keys(beforeData),
    ...Object.keys(afterData),
  ])

  return [...fields]
    .filter((field) => !HISTORY_IGNORED_FIELDS.has(field))
    .map((field) => ({
      field,
      beforeValue: getHistoryFieldValue(beforeData, field),
      afterValue: getHistoryFieldValue(afterData, field),
    }))
    .filter(
      ({ beforeValue, afterValue }) =>
        beforeValue !== undefined || afterValue !== undefined,
    )
    .filter(
      ({ beforeValue, afterValue }) =>
        JSON.stringify(beforeValue) !== JSON.stringify(afterValue),
    )
    .map((field) => ({
      field: field.field,
      label: HISTORY_FIELD_LABELS[field.field] || field.field,
      before: formatHistoryValue(field.field, field.beforeValue),
      after: formatHistoryValue(field.field, field.afterValue),
    }))
}

function normalizeInvoiceAutoConfig(config) {
  if (!config) {
    return {
      ...AppData.default_invoiceAutoConfig,
      isAuto: false,
      typeTime: TYPE_TIME.LOG,
      timeMoment: [],
      timeLog: 1,
      allSerials: false,
      serials: [],
    }
  }

  let serials = config.serials
  if (typeof serials === 'string') {
    try {
      serials = JSON.parse(serials)
    } catch {
      serials = serials
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
  }

  let timeMoment = config.timeMoment || config.autoSentTime
  if (typeof timeMoment === 'string') {
    if (timeMoment.includes(',')) {
      timeMoment = timeMoment
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    } else if (timeMoment.startsWith('[') && timeMoment.endsWith(']')) {
      try {
        timeMoment = JSON.parse(timeMoment)
      } catch {
        timeMoment = [timeMoment]
      }
    } else if (timeMoment.trim()) {
      timeMoment = [timeMoment.trim()]
    } else {
      timeMoment = []
    }
  } else if (!Array.isArray(timeMoment) || timeMoment.length === 0) {
    timeMoment = []
  }

  return {
    ...config,
    isAuto: Boolean(config.isAuto),
    typeTime: config.typeTime || TYPE_TIME.LOG,
    timeMoment: Array.isArray(timeMoment) ? timeMoment : [timeMoment],
    timeLog: config.timeLog ?? 15,
    allSerials: Boolean(config.allSerials),
    serials: Array.isArray(serials) ? serials : [],
    typeInvoice: config.typeInvoice || SYSTEM_TYPE_INVOICE.SINGLE,
  }
}

function InvoiceSerialSelect({
  serials = [],
  selectedSerials = [],
  allSerials = false,
  onChange,
  isLoading,
  disabled = false,
}) {
  const options = useMemo(
    () => [
      {
        value: ALL_SERIALS_VALUE,
        label: 'Tất cả',
        shortLabel: 'Tất cả',
        searchText: 'Tất cả Áp dụng cho tất cả mẫu hóa đơn',
      },
      ...serials
        .filter((item) => item?.value !== null && item?.serial)
        .map((item) => ({
          value: item.serial,
          label: `${item.serial} - ${item.invoiceTemplateName || item.label || item.serial}`,
          shortLabel: item.serial,
          searchText: `${item.serial} ${item.invoiceTemplateName || item.label || ''}`,
        })),
    ],
    [serials],
  )

  const handleChange = (values = []) => {
    if (values.includes(ALL_SERIALS_VALUE) && !allSerials) {
      onChange({ allSerials: true, serials: [] })
      return
    }

    onChange({
      allSerials: false,
      serials: values.filter((value) => value !== ALL_SERIALS_VALUE),
    })
  }

  return (
    <CustomAntSelect
      className="serial-select-input"
      allowClear
      disabled={disabled}
      isLoading={isLoading}
      isMultiSelect
      maxTagCount={3}
      notFoundContent="Không tìm thấy mẫu hóa đơn phù hợp"
      optionLabelProp="shortLabel"
      options={options}
      placeholder="Chọn hoặc tìm kiếm"
      value={allSerials ? [ALL_SERIALS_VALUE] : selectedSerials}
      customFilterOptionFn={(input, option) =>
        option.searchText.toLowerCase().includes(input.trim().toLowerCase())
      }
      onChange={handleChange}
    />
  )
}

export default function TabInvoiceAutoSetting() {
  const { invoiceAutoConfig } = useSystemConfigSelector()
  const dispatch = useAppDispatch()
  const [draftWarning, setDraftWarning] = useState({
    show: false,
    values: null,
    count: 0,
  })
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [historyItems, setHistoryItems] = useState([])
  const [historyFilter, setHistoryFilter] = useState({ page: 1, limit: 20 })
  const [historyTotalItems, setHistoryTotalItems] = useState(0)
  const { data: serials, isLoading: isGettingSerial } = useGetSerials(
    'HD,MTT',
    1,
  )

  useEffect(() => {
    dispatch(getInvoiceAutoConfig())
  }, [dispatch])

  const handleSaveChange = useMutation({
    mutationKey: ['systemConfig', 'invoiceAutoConfig'],
    mutationFn: (values) => {
      const payload = {
        ...values,
        timeMoment: Array.isArray(values.timeMoment)
          ? values.timeMoment.join(',')
          : values.timeMoment,
        autoSentTime: Array.isArray(values.timeMoment)
          ? values.timeMoment.join(',')
          : values.timeMoment,
        serials: values.allSerials ? [] : (values.serials ?? []),
      }
      return dispatch(updateInvoiceAutoConfig(payload)).unwrap()
    },
    onSuccess: async () => {
      ToastHelper.showSuccess('Cập nhật thành công')
      await dispatch(getInvoiceAutoConfig())
    },
  })

  const checkDraftBeforeConfig = useMutation({
    mutationKey: ['systemConfig', 'draftBeforeConfigCount'],
    mutationFn: () => dispatch(getDraftBeforeConfigCount()).unwrap(),
  })

  const invoiceAutoConfigHistory = useMutation({
    mutationKey: ['systemConfig', 'invoiceAutoConfigHistory'],
    mutationFn: async (filter) => {
      const fetchHistoryPage = async (pageFilter) => {
        const response = await dispatch(
          getInvoiceAutoConfigHistory(pageFilter),
        ).unwrap()

        if (
          response?.result !== 'success' ||
          !Array.isArray(response?.data?.items) ||
          !Number.isInteger(response?.data?.totalItems)
        ) {
          throw new Error('Không thể tải lịch sử chỉnh sửa cấu hình')
        }

        return response.data
      }

      const data = await fetchHistoryPage(filter)

      if (filter.page !== 1) {
        return data
      }

      const createRecordInPage = data.items.find(
        (item) => item.action === 'CREATE',
      )
      if (createRecordInPage) {
        return {
          ...data,
          items: [
            createRecordInPage,
            ...data.items.filter((item) => item !== createRecordInPage),
          ],
        }
      }

      if (data.totalItems === 0) return data

      const lastPage = Math.ceil(data.totalItems / filter.limit)
      const oldestPage =
        lastPage === filter.page
          ? data
          : await fetchHistoryPage({ ...filter, page: lastPage })
      const oldestRecord = oldestPage.items[oldestPage.items.length - 1]

      if (!oldestRecord) return data

      const createRecord =
        oldestRecord.action === 'CREATE'
          ? oldestRecord
          : {
              invoiceAutoConfigHistoryId: `create-${oldestRecord.invoiceAutoConfigId}`,
              action: 'CREATE',
              changedBy: oldestRecord.beforeData?.accountId,
              beforeData: {},
              afterData: oldestRecord.beforeData ?? {},
              createdAt:
                oldestRecord.beforeData?.createdAt ?? oldestRecord.createdAt,
            }

      return {
        ...data,
        items: [...data.items, createRecord],
        totalItems:
          oldestRecord.action === 'CREATE'
            ? data.totalItems
            : data.totalItems + 1,
      }
    },
    onSuccess: (data) => {
      setHistoryItems(
        data.items.map((item) => ({
          ...item,
          id: item.invoiceAutoConfigHistoryId,
        })),
      )
      setHistoryFilter((previous) => ({
        ...previous,
        page: data.currentPage,
        limit: data.limit ?? previous.limit,
      }))
      setHistoryTotalItems(data.totalItems)
    },
    onError: (error) => {
      setHistoryItems([])
      setHistoryTotalItems(0)
      ToastHelper.showError(
        error?.message || 'Không thể tải lịch sử chỉnh sửa cấu hình',
      )
    },
  })

  const historyColumns = useMemo(
    () => [
      {
        name: 'Thời gian',
        width: '180px',
        cell: (row) =>
          dayjs(row.createdAt).isValid()
            ? dayjs(row.createdAt).format('DD/MM/YYYY HH:mm:ss')
            : '',
      },
      {
        name: 'Thao tác',
        width: '120px',
        cell: (row) =>
          row.action === 'CREATE'
            ? 'Tạo mới'
            : row.action === 'UPDATE'
              ? 'Cập nhật'
              : row.action || '',
      },
      {
        name: 'Nội dung chỉnh sửa',
        minWidth: '400px',
        cell: (row) => {
          const changes = getHistoryChanges(row)

          if (changes.length === 0) return ''

          return (
            <div>
              {changes.map(({ field, label, before, after }) => (
                <div key={field}>
                  <strong>{label}:</strong> {before} → {after}
                </div>
              ))}
            </div>
          )
        },
      },
    ],
    [],
  )

  const openHistoryModal = () => {
    const initialFilter = { page: 1, limit: 20 }
    setHistoryModalOpen(true)
    setHistoryItems([])
    setHistoryFilter(initialFilter)
    setHistoryTotalItems(0)
    invoiceAutoConfigHistory.mutate(initialFilter)
  }

  return (
    <Formik
      initialValues={normalizeInvoiceAutoConfig(invoiceAutoConfig)}
      enableReinitialize
      onSubmit={async (values) => {
        const response = await checkDraftBeforeConfig.mutateAsync()
        const count = response?.data?.count

        if (
          response?.result !== 'success' ||
          !Number.isInteger(count) ||
          count < 0
        ) {
          ToastHelper.showError('Không thể kiểm tra số hóa đơn nháp chưa xuất')
          return
        }

        if (count > 0) {
          setDraftWarning({ show: true, values, count })
          return
        }

        handleSaveChange.mutate(values)
      }}
    >
      {(formikProps) => {
        const isAuto = Boolean(formikProps.values.isAuto)

        return (
          <Flex
            justify={'space-between'}
            vertical
            className="w-100 h-100 tab-invoice-auto-setting"
          >
            <ModalConfirm
              show={draftWarning.show}
              setShow={(show) =>
                setDraftWarning((previous) => ({ ...previous, show }))
              }
              header="Hóa đơn nháp chưa được xuất"
              content={
                <>
                  Có <strong>{draftWarning.count}</strong> hóa đơn nháp chưa
                  được xuất trước thời điểm cấu hình. Các hóa đơn này sẽ không
                  được tự động phát hành. Bạn có muốn tiếp tục lưu cấu hình
                  không?
                </>
              }
              handleConfirm={() => {
                handleSaveChange.mutate(draftWarning.values)
                setDraftWarning({ show: false, values: null, count: 0 })
              }}
              confirmText="Tiếp tục"
            />
            <Modal
              title="Lịch sử chỉnh sửa cấu hình"
              open={historyModalOpen}
              onCancel={() => setHistoryModalOpen(false)}
              footer={null}
              width="min(1100px, 95vw)"
              centered
            >
              <CustomDataTable
                columns={historyColumns}
                dataSource={historyItems}
                isLoading={invoiceAutoConfigHistory.isPending}
                scrollY="500px"
                pagination={{
                  currentPage: historyFilter.page,
                  rowsPerPage: historyFilter.limit,
                  total: historyTotalItems,
                  onChangePage: (page) => {
                    const filter = { ...historyFilter, page: Number(page) }
                    setHistoryFilter(filter)
                    invoiceAutoConfigHistory.mutate(filter)
                  },
                  onChangeRowsPerPage: (limit) => {
                    const filter = { ...historyFilter, page: 1, limit }
                    setHistoryFilter(filter)
                    invoiceAutoConfigHistory.mutate(filter)
                  },
                }}
              />
            </Modal>
            <section>
              <div className="mb-4">
                <Typography.Title level={5} style={{ marginBottom: 4 }}>
                  Cấu hình tự động phát hành hóa đơn
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Thiết lập hệ thống tự động phát hành hóa đơn nháp theo điều
                  kiện và thời gian bạn chọn.
                </Typography.Text>
              </div>

              <div className="auto-invoice-section">
                <div className="section-header-flex">
                  <div>
                    <div className="section-title">
                      Bật tự động phát hành{' '}
                      <span className="required-star">*</span>
                    </div>
                    <div className="section-desc">
                      Tự động phát hành hóa đơn nháp theo cấu hình bên dưới.{' '}
                      <span className="hsm-highlight">
                        Chỉ áp dụng cho chữ ký số HSM.
                      </span>
                    </div>
                  </div>
                  <div>
                    <Switch
                      checked={formikProps.values.isAuto}
                      onChange={(checked) =>
                        formikProps.setFieldValue('isAuto', checked)
                      }
                      style={{
                        backgroundColor: formikProps.values.isAuto
                          ? '#1890ff'
                          : undefined,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`auto-invoice-dependent-content ${
                  !isAuto ? 'is-disabled' : ''
                }`}
              >
                <div className="auto-invoice-section">
                  <div className="section-header-flex">
                    <div>
                      <div className="section-title">
                        Mẫu hóa đơn áp dụng{' '}
                        <span className="required-star">*</span>
                      </div>
                      <div className="section-desc">
                        Chọn mẫu hóa đơn sẽ được áp dụng.
                      </div>
                    </div>
                    <div>
                      <InvoiceSerialSelect
                        serials={serials}
                        isLoading={isGettingSerial}
                        selectedSerials={formikProps.values.serials ?? []}
                        allSerials={formikProps.values.allSerials}
                        disabled={!isAuto}
                        onChange={({ allSerials, serials: newSerials }) => {
                          formikProps.setFieldValue('allSerials', allSerials)
                          formikProps.setFieldValue('serials', newSerials)
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="auto-invoice-section">
                  <div className="section-title">
                    Cơ chế phát hành <span className="required-star">*</span>
                  </div>
                  <div className="section-desc">
                    Chọn cơ chế tự động phát hành hóa đơn nháp.
                  </div>

                  <div className="mechanism-grid">
                    <div
                      className={`mechanism-card ${
                        formikProps.values.typeTime === TYPE_TIME.LOG
                          ? 'active'
                          : ''
                      } ${!isAuto ? 'is-disabled' : ''}`}
                      onClick={() => {
                        if (!isAuto) return
                        formikProps.setFieldValue('typeTime', TYPE_TIME.LOG)
                      }}
                    >
                      <div className="radio-dot">
                        {formikProps.values.typeTime === TYPE_TIME.LOG && (
                          <div className="radio-inner" />
                        )}
                      </div>
                      <div>
                        <div className="mech-title">Chờ xuất</div>
                        <div className="mech-desc">
                          Phát hành sau khi đủ thời gian chờ.
                        </div>
                      </div>
                    </div>

                    <div
                      className={`mechanism-card ${
                        formikProps.values.typeTime === TYPE_TIME.MOMENT
                          ? 'active'
                          : ''
                      } ${!isAuto ? 'is-disabled' : ''}`}
                      onClick={() => {
                        if (!isAuto) return
                        formikProps.setFieldValue('typeTime', TYPE_TIME.MOMENT)
                      }}
                    >
                      <div className="radio-dot">
                        {formikProps.values.typeTime === TYPE_TIME.MOMENT && (
                          <div className="radio-inner" />
                        )}
                      </div>
                      <div>
                        <div className="mech-title">Theo khung giờ</div>
                        <div className="mech-desc">
                          Tự động phát hành vào các khung giờ đã thiết lập.
                        </div>
                      </div>
                    </div>
                  </div>

                  {formikProps.values.typeTime === TYPE_TIME.MOMENT && (
                    <div className="scheduled-times-wrapper">
                      <div className="schedule-label">
                        Khung giờ phát hành{' '}
                        <span className="required-star">*</span>
                      </div>
                      <div className="time-tags-list">
                        {(formikProps.values.timeMoment ?? []).map((t) => (
                          <span key={t} className="time-pill">
                            {t}
                            {isAuto && (
                              <CloseOutlined
                                className="close-icon"
                                onClick={() => {
                                  const updated = (
                                    formikProps.values.timeMoment ?? []
                                  ).filter((time) => time !== t)
                                  formikProps.setFieldValue(
                                    'timeMoment',
                                    updated,
                                  )
                                }}
                              />
                            )}
                          </span>
                        ))}
                        <TimePicker
                          className="add-time-picker"
                          placeholder="Thêm giờ"
                          format="HH:mm"
                          hourStep={1}
                          minuteStep={5}
                          changeOnBlur
                          allowClear={false}
                          disabled={!isAuto}
                          value={null}
                          suffixIcon={
                            <PlusOutlined
                              style={{
                                fontSize: 11,
                                color: isAuto ? '#1890ff' : '#bfbfbf',
                              }}
                            />
                          }
                          onChange={(val, timeString) => {
                            if (!timeString) return
                            const currentTimes =
                              formikProps.values.timeMoment ?? []
                            if (currentTimes.includes(timeString)) {
                              ToastHelper.showWarning(
                                `Khung giờ ${timeString} đã tồn tại`,
                              )
                              return
                            }
                            const updated = [...currentTimes, timeString].sort()
                            formikProps.setFieldValue('timeMoment', updated)
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {formikProps.values.typeTime === TYPE_TIME.LOG && (
                    <div className="time-config-row">
                      <div className="time-config-label">
                        <span>Thời gian chờ xuất</span>
                        <span className="required-star">*</span>
                      </div>
                      <div className="time-config-input">
                        <CustomAntInput
                          type="number"
                          borderRadius="sm"
                          inputProps={{
                            className: 'max-w-300px',
                            addonAfter: 'phút',
                            value: formikProps.values.timeLog,
                            disabled: !isAuto,
                            onChange: (inputValue) => {
                              formikProps.setFieldValue('timeLog', inputValue)
                            },
                            placeholder: 'Min: 1, max: 999',
                            min: 1,
                            max: 999,
                            style: {
                              width: '100%',
                              padding: 0,
                            },
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Alert
                type="warning"
                showIcon
                className="mt-4 mb-4"
                message="Hệ thống chỉ xuất tự động các hóa đơn nháp từ thời điểm cấu hình trở đi — quý khách vui lòng chủ động phát hành các hóa đơn nháp trước thời điểm cấu hình."
              />
            </section>

            <FooterCard
              onRestoreDefault={() => {
                _.entries(
                  normalizeInvoiceAutoConfig(AppData.default_invoiceAutoConfig),
                  ([key, value]) => {
                    formikProps.setFieldValue(key, value)
                  },
                )
              }}
              onRestoreChange={formikProps.resetForm}
              onSave={formikProps.submitForm}
              isLoading={
                formikProps.isSubmitting ||
                checkDraftBeforeConfig.isPending ||
                handleSaveChange.isPending
              }
              show={formikProps.dirty}
              afterRestoreDefault={
                <CustomAntButton
                  text="Xem lịch sử chỉnh sửa"
                  antProps={{
                    type: 'primary',
                    ghost: true,
                    onClick: openHistoryModal,
                  }}
                />
              }
            />
          </Flex>
        )
      }}
    </Formik>
  )
}
